// backend/constants/roles.js

/**
 * Hierarchical permission levels for resources.
 * Higher values grant broader access.
 */
export const PERMISSION_LEVELS = {
    NONE: 0,      // No access
    OWN: 1,       // Only own items
    ASSIGNED: 2,  // Own + assigned items
    ORG: 3,       // Organization-wide
    ALL: 4        // System-wide
};

/**
 * Role definitions with granular permissions.
 * Format: { id, name, userType, permissions: { resource: { view, edit } } }
 */
export const ROLE_DEFINITIONS = {
    // INTERNAL STAFF
    SYSTEM_ADMIN: {
        id: 1,
        name: 'System Admin',
        userType: 'internal_staff',
        permissions: {
            tickets: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.ALL },
            settings: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.ALL },
            accountRequests: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.ALL }
        }
    },
    INTERNAL_SUPERVISOR: {
        id: 2,
        name: 'Internal Staff Supervisor',
        userType: 'internal_staff',
        permissions: {
            tickets: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.ALL },
            settings: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.ALL },
            accountRequests: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.ALL }
        }
    },
    INTERNAL_STANDARD: {
        id: 3,
        name: 'Internal Staff Standard',
        userType: 'internal_staff',
        permissions: {
            tickets: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.ASSIGNED },
            settings: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.NONE }
        }
    },

    // PARTNERS
    PARTNER_SUPERVISOR: {
        id: 4,
        name: 'Partner Supervisor',
        userType: 'partner',
        permissions: {
            tickets: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.ORG },
            settings: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.NONE }
        }
    },
    PARTNER_STANDARD: {
        id: 5,
        name: 'Partner Standard',
        userType: 'partner',
        permissions: {
            tickets: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.ASSIGNED },
            settings: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.NONE }
        }
    },
    PARTNER_GLOBAL_VIEWER: {
        id: 9,
        name: 'Partner Global Viewer',
        userType: 'partner',
        permissions: {
            tickets: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.NONE },
            settings: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.NONE }
        }
    },

    // CUSTOMERS
    CUSTOMER_SUPERVISOR: {
        id: 6,
        name: 'Customer Supervisor',
        userType: 'customer',
        permissions: {
            tickets: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.ORG },
            settings: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.NONE }
        }
    },
    CUSTOMER_STANDARD: {
        id: 7,
        name: 'Customer Standard',
        userType: 'customer',
        permissions: {
            tickets: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.OWN },
            settings: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.NONE }
        }
    },
    CUSTOMER_GLOBAL_VIEWER: {
        id: 8,
        name: 'Customer Global Viewer',
        userType: 'customer',
        permissions: {
            tickets: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.NONE },
            settings: { view: PERMISSION_LEVELS.ALL, edit: PERMISSION_LEVELS.NONE }
        }
    }
};

export const resolveUserRole = (user) => {
    if (!user) return { key: null, name: null, definition: null };
    
    // Case 1: Already has normalized role key (e.g., "PARTNER_SUPERVISOR")
    if (user.role && ROLE_DEFINITIONS[user.role]) {
        return {
            key: user.role,
            name: ROLE_DEFINITIONS[user.role].name,
            definition: ROLE_DEFINITIONS[user.role]
        };
    }
    
    // Case 2: Has roleName (display name like "Partner Supervisor")
    if (user.roleName) {
        for (const [key, definition] of Object.entries(ROLE_DEFINITIONS)) {
            if (definition.name === user.roleName) {
                return { key, name: user.roleName, definition };
            }
        }
    }
    
    // Case 3: Has role ID
    if (user.roleId) {
        for (const [key, definition] of Object.entries(ROLE_DEFINITIONS)) {
            if (definition.id === user.roleId) {
                return { key, name: definition.name, definition };
            }
        }
    }
    
    // Case 4: Try to find by any possible field
    const roleValue = user.role || user.roleName || user.roleId;
    if (roleValue) {
        // Try as key
        if (ROLE_DEFINITIONS[roleValue]) {
            return {
                key: roleValue,
                name: ROLE_DEFINITIONS[roleValue].name,
                definition: ROLE_DEFINITIONS[roleValue]
            };
        }
        
        // Try as name
        for (const [key, definition] of Object.entries(ROLE_DEFINITIONS)) {
            if (definition.name === roleValue) {
                return { key, name: definition.name, definition };
            }
        }
        
        // Try as ID
        const numericId = typeof roleValue === 'number' ? roleValue : parseInt(roleValue);
        if (!isNaN(numericId)) {
            for (const [key, definition] of Object.entries(ROLE_DEFINITIONS)) {
                if (definition.id === numericId) {
                    return { key, name: definition.name, definition };
                }
            }
        }
    }
    
    return { key: null, name: null, definition: null };
};

/**
 * Universal canView - works with any user object format
 */
export const canView = (user, resourceType, requiredLevel = PERMISSION_LEVELS.NONE) => {
    const { definition } = resolveUserRole(user);
    return definition?.permissions[resourceType]?.view >= requiredLevel;
};

/**
 * Universal canEdit - works with any user object format
 */
export const canEdit = (user, resourceType, requiredLevel) => {
    const { definition } = resolveUserRole(user);
    const defaultLevel = resourceType === 'settings' 
        ? PERMISSION_LEVELS.ALL 
        : PERMISSION_LEVELS.OWN;
    const effectiveLevel = requiredLevel !== undefined ? requiredLevel : defaultLevel;
    return definition?.permissions[resourceType]?.edit >= effectiveLevel;
};

/**
 * Universal canEditTicket - works with any user object format
 */
export const canEditTicket = (user, ticketData) => {
    if (!user || !ticketData) return false;
    
    const { key: roleKey, name: roleName } = resolveUserRole(user);
    
    console.log('canEditTicket check:', {
        userId: user.id,
        userRole: roleKey,
        userRoleName: roleName,
        ticketCreatedBy: ticketData.createdByUserId,
        ticketAssignedTo: ticketData.assignedToUserId,
        userOrgId: user.organizationId,
        ticketOrgId: ticketData.organizationId,
        isSameOrg: user.organizationId === ticketData.organizationId,
        canEditAll: canEdit(user, 'tickets', PERMISSION_LEVELS.ALL),
        canEditOrg: canEdit(user, 'tickets', PERMISSION_LEVELS.ORG),
        canEditAssigned: canEdit(user, 'tickets', PERMISSION_LEVELS.ASSIGNED),
        canEditOwn: canEdit(user, 'tickets', PERMISSION_LEVELS.OWN)
    });
    
    // God mode - system admins can edit everything
    if (canEdit(user, 'tickets', PERMISSION_LEVELS.ALL)) {
        console.log('canEditTicket: ALL permission granted');
        return true;
    }
    
    // Organization-wide edit permission
    if (canEdit(user, 'tickets', PERMISSION_LEVELS.ORG)) {
        const isSameOrganization = user.organizationId && 
                                 ticketData.organizationId && 
                                 user.organizationId === ticketData.organizationId;
        
        if (isSameOrganization) {
            // Creator or assignee can always manage
            const isCreator = user.id === ticketData.createdByUserId;
            const isAssignee = user.id === ticketData.assignedToUserId;
            if (isCreator || isAssignee) {
                console.log('canEditTicket: Creator/Assignee in same org - ALLOW');
                return true;
            }
            
            // For same organization users with appropriate roles - USE RESOLVED roleName
            const isPartnerSupervisor = roleName === 'Partner Supervisor';
            const isCustomerSupervisor = roleName === 'Customer Supervisor';
            
            const result = isPartnerSupervisor || isCustomerSupervisor;
            console.log('canEditTicket: Supervisor Check -', result ? 'ALLOW' : 'DENY');
            
            return result;
        }
        
        console.log('canEditTicket: ORG permission check - different org');
        return false;
    }
    
    // Assigned items edit permission
    if (canEdit(user, 'tickets', PERMISSION_LEVELS.ASSIGNED)) {
        const isAssignedOrCreated = ticketData.assignedToUserId === user.id || 
                                   ticketData.createdByUserId === user.id;
        console.log('canEditTicket: ASSIGNED permission check', { 
            isAssignedOrCreated, 
            assignedTo: ticketData.assignedToUserId, 
            createdBy: ticketData.createdByUserId,
            userId: user.id
        });
        return isAssignedOrCreated;
    }
    
    // Own items only
    if (canEdit(user, 'tickets', PERMISSION_LEVELS.OWN)) {
        const isOwner = ticketData.createdByUserId === user.id;
        console.log('canEditTicket: OWN permission check', { 
            isOwner, 
            createdBy: ticketData.createdByUserId,
            userId: user.id
        });
        return isOwner;
    }
    
    console.log('canEditTicket: No permission granted');
    return false;
};

// ================= KEEP ALL OTHER FUNCTIONS AS-IS =================

/**
 * Checks if user has product access
 */
export const hasProductAccess = (user, productId, contractId) => {
    if (!user || !productId) return false;
    if (canEdit(user, 'tickets', PERMISSION_LEVELS.ALL)) return true;
    
    return user.assignedProducts?.some(p => p.id === productId) ||
           user.accessibleContracts?.some(c => 
               c.id === contractId && 
               c.products?.some(p => p.id === productId)
           );
};

// Helper functions
export const isGodMode = (user) => canEdit(user, 'tickets', PERMISSION_LEVELS.ALL);
export const canGenerateReport = (user) => canView(user, 'tickets', PERMISSION_LEVELS.ORG);
export const isAssigneeDisabled = (user) => !canEdit(user, 'tickets', PERMISSION_LEVELS.ASSIGNED);

// Pre-computed role lists
export const getRolesWithPermission = (resource, action, requiredLevel) => {
    return Object.keys(ROLE_DEFINITIONS)
        .filter(roleKey => ROLE_DEFINITIONS[roleKey].permissions[resource]?.[action] >= requiredLevel)
        .map(roleKey => ROLE_DEFINITIONS[roleKey].name);
};

export const ALLOWED_TICKET_EDITORS = getRolesWithPermission('tickets', 'edit', PERMISSION_LEVELS.OWN);
export const ALLOWED_TICKET_VIEWERS = getRolesWithPermission('tickets', 'view', PERMISSION_LEVELS.OWN);
export const ALLOWED_SETTINGS_ADMINS = getRolesWithPermission('settings', 'edit', PERMISSION_LEVELS.ALL);
export const ALLOWED_SETTINGS_VIEWERS = getRolesWithPermission('settings', 'view', PERMISSION_LEVELS.ALL);

// Role metadata
export const ROLE_NAMES = Object.fromEntries(
    Object.entries(ROLE_DEFINITIONS).map(([key, val]) => [key, val.name])
);
export const ROLE_IDS = Object.fromEntries(
    Object.entries(ROLE_DEFINITIONS).map(([key, val]) => [key, val.id])
);
export const ROLE_USER_TYPES = Object.fromEntries(
    Object.entries(ROLE_DEFINITIONS).map(([key, val]) => [key, val.userType])
);

// Export the universal resolution function too


// ==============================================
// FRONTEND-SPECIFIC ENHANCEMENTS (NOT IN BACKEND)
// ==============================================

// Reverse mapping from display name to role key
export const ROLE_DISPLAY_NAME_TO_KEY = Object.fromEntries(
  Object.entries(ROLE_NAMES).map(([key, name]) => [name, key])
);

// Reverse mapping from ID to role key
export const ROLE_ID_TO_KEY = Object.fromEntries(
  Object.entries(ROLE_IDS).map(([key, id]) => [id, key])
);

// Enhanced normalization function for frontend use
export const normalizeRole = (role) => {
  if (!role) return null;

  // Case 1: already correct key (e.g. "SYSTEM_ADMIN")
  if (ROLE_DEFINITIONS[role]) return role;

  // Case 2: numeric id
  const roleId = typeof role === 'number' ? role : parseInt(role);
  if (!isNaN(roleId) && ROLE_ID_TO_KEY[roleId]) {
    return ROLE_ID_TO_KEY[roleId];
  }

  // Case 3: display name (e.g. "System Admin")
  if (ROLE_DISPLAY_NAME_TO_KEY[role]) {
    return ROLE_DISPLAY_NAME_TO_KEY[role];
  }

  // Case 4: case-insensitive match
  const lowerCaseRole = String(role).toLowerCase();
  for (const [displayName, roleKey] of Object.entries(ROLE_DISPLAY_NAME_TO_KEY)) {
    if (displayName.toLowerCase() === lowerCaseRole) {
      return roleKey;
    }
  }

  return null;
};

// Alias for ROLES (if your AuthContext expects this)
export const ROLES = ROLE_DEFINITIONS;
